<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationFollowUpsAPI from 'dashboard/api/conversationFollowUps';

const props = defineProps({
  enrollment: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['changed']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedFlowId = ref(null);
const isSubmitting = ref(false);

const flows = useMapGetter('followUpFlows/getFlows');

// The active catalog, with the cycle's own flow kept on top even when it is
// paused — the select must always be able to show the current membership.
const flowOptions = computed(() => {
  const active = (flows.value || []).filter(flow => flow.status === 'active');
  if (!props.enrollment) return active;
  if (active.some(flow => flow.id === props.enrollment.flow_id)) return active;
  return [
    { id: props.enrollment.flow_id, name: props.enrollment.flow_name },
    ...active,
  ];
});

const isPausedCycle = computed(() => props.enrollment?.status === 'paused');
const hasFlowChange = computed(
  () =>
    props.enrollment &&
    selectedFlowId.value !== null &&
    selectedFlowId.value !== props.enrollment.flow_id
);

const open = () => {
  selectedFlowId.value = props.enrollment?.flow_id ?? null;
  isSubmitting.value = false;
  dialogRef.value?.open();
};

defineExpose({ open });

const errorMessage = error =>
  error?.response?.data?.error || t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.ERROR');

const finish = message => {
  useAlert(message);
  emit('changed');
  dialogRef.value?.close();
};

const togglePause = async () => {
  if (!props.enrollment || isSubmitting.value) return;
  isSubmitting.value = true;
  const { conversation_display_id: conversationId, enrollment_id: id } =
    props.enrollment;
  try {
    if (isPausedCycle.value) {
      await ConversationFollowUpsAPI.resume(conversationId, id);
      finish(t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.RESUMED'));
    } else {
      await ConversationFollowUpsAPI.pause(conversationId, id);
      finish(t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.PAUSED'));
    }
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSubmitting.value = false;
  }
};

// Cancelling the cycle is irreversible: the trash asks first, like the
// conversation panel and the contact profile do.
const removeConfirmDialogRef = ref(null);

const requestRemove = async () => {
  if (!props.enrollment || isSubmitting.value) return;
  dialogRef.value?.close();
  await nextTick();
  removeConfirmDialogRef.value?.open();
};

const confirmRemove = async () => {
  if (!props.enrollment || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await ConversationFollowUpsAPI.remove(
      props.enrollment.conversation_display_id,
      props.enrollment.enrollment_id
    );
    useAlert(t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.REMOVED'));
    emit('changed');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSubmitting.value = false;
    removeConfirmDialogRef.value?.close();
  }
};

// Moving = remove the current cycle, then enroll on the chosen flow. The
// first half failing changes nothing (the dialog stays). Once the removal
// committed, EVERY outcome of the second half is a state change: the dialog
// must report it, refresh the row and close — never keep offering actions on
// the enrollment that no longer exists.
const handleConfirm = async () => {
  if (!hasFlowChange.value) {
    dialogRef.value?.close();
    return;
  }
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await ConversationFollowUpsAPI.remove(
      props.enrollment.conversation_display_id,
      props.enrollment.enrollment_id
    );
  } catch (error) {
    useAlert(errorMessage(error));
    isSubmitting.value = false;
    return;
  }
  try {
    const response = await store.dispatch('followUpFlows/enrollContacts', {
      flowId: selectedFlowId.value,
      contactIds: [props.enrollment.contact_id],
    });
    const disposition = response?.results?.[0]?.disposition;
    if (disposition === 'created') {
      finish(t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.MOVED'));
    } else {
      finish(t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.MOVE_NOT_ENROLLED'));
    }
  } catch {
    // transient failure between the two halves: the removal stands
    finish(t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.MOVE_PARTIAL'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.TITLE')"
    :description="t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.DESCRIPTION')"
    :confirm-button-label="t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.SAVE')"
    :disable-confirm-button="!hasFlowChange"
    :is-loading="isSubmitting"
    @confirm="handleConfirm"
  >
    <div v-if="enrollment" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.FLOW_LABEL') }}
      </span>
      <div class="flex items-center gap-2">
        <select
          v-model="selectedFlowId"
          class="reset-base flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-n-weak bg-white dark:bg-slate-900 text-n-slate-12"
        >
          <option v-for="flow in flowOptions" :key="flow.id" :value="flow.id">
            {{ flow.name }}
          </option>
        </select>
        <Button
          size="sm"
          variant="faded"
          :color="isPausedCycle ? 'blue' : 'amber'"
          :icon="isPausedCycle ? 'i-lucide-play' : 'i-lucide-pause'"
          :title="
            isPausedCycle
              ? t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.RESUME')
              : t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.PAUSE')
          "
          :disabled="isSubmitting"
          @click="togglePause"
        />
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          icon="i-lucide-trash-2"
          :title="t('CONTACTS_LAYOUT.FOLLOW_UP_EDIT.REMOVE')"
          :disabled="isSubmitting"
          @click="requestRemove"
        />
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="removeConfirmDialogRef"
    type="alert"
    :title="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_CONFIRM.TITLE')"
    :description="
      t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_CONFIRM.DESCRIPTION')
    "
    :confirm-button-label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE')"
    :is-loading="isSubmitting"
    @confirm="confirmRemove"
  />
</template>
